// Detects "a pick just happened" from state alone, and decides how much of a
// celebration it has earned.
//
// **Detection.** The provider hands the board a DraftState, not a stream of
// cable events — useDraftChannel folds pick_made into the reducer and the page
// never sees the event. So the flash is driven by diffing the set of made
// overalls against the previous render's set:
//
//   * first observation is skipped entirely — on load (or after a gap-detection
//     refetch that arrives before any local state) the whole board is "new",
//     and flashing 90 picks in a row is not the intent
//   * only *additions* trigger; an undo removes an overall and must not flash
//   * if several appear at once (a refetch that caught up on missed events)
//     the highest overall wins, which is the most recent pick
//
// **Duration.** The pre-roll is worth 760ms of everyone's attention exactly when
// there has been something to anticipate. Back to back — an autopick storm, a
// commissioner clearing a stalled round — a fresh pick tears the current flash
// down mid-beat, so the drum roll never finishes and the name never lands. Those
// picks skip the bar and get the pre-#65 2500ms instead.
//
// The measurement is *when the last flash ended*, not when the last pick landed.
// Picks arriving 2s apart during a 3.1s flash never leave the board uncovered at
// all, and a "since last pick" rule would compare against a timestamp that keeps
// resetting while the screen is continuously covered. The question actually
// being asked is whether the viewer has had a moment of grid since the last one.
//
// Nothing here touches useDraftChannel or draftReducer — they are shared with
// the DraftRoom and the commissioner bar.

import { useEffect, useRef, useState } from 'react'
import type { PickState } from '../../types/protocol'

// 4950ms: the anticipation bar owns the first 1800 — 1220 of it holding THE
// PICK IS IN still, which is the part you actually read — the reveal beats run
// to 2610, and the card then holds for ~1.3s before flying into its cell
// between 3900 and 4500. Grown off the TV from 3150: the bar was spending more
// of its life moving than holding, and even once that was fixed the line went
// by faster than a room full of people looks up.
//
// The last 450ms are the flare's. Under the old budget the ring was scheduled
// past the unmount and never appeared at all; the tail is what lets it expand
// on screen rather than being clipped a frame after it arrives.
const FULL_MS = 4950
// The fast path is exactly the timing that predates the pre-roll, which is the
// other half of why gating it is worth doing: the storm case is no worse than
// it ever was, rather than 35% worse on every pick.
const FAST_MS = 2500

// How long the grid must have been uncovered for the next pick to earn the full
// sequence. Deliberately generous: 4s is longer than any single beat, so a
// "quiet" board is one a viewer actually looked at, not one that blinked.
const QUIET_MS = 4000

export interface PickFlashState {
  pick: PickState
  /** False when picks are landing on top of each other — skip the drum roll. */
  preroll: boolean
}

export function usePickFlash(
  /** null until the draft has loaded — a loading board has not "seen" anything. */
  picks: PickState[] | null | undefined,
): PickFlashState | null {
  const seenRef = useRef<Set<number> | null>(null)
  // When the last overlay came down. Not when the last pick landed: picks
  // arriving 2s apart during a 3.1s flash never leave the board uncovered at
  // all, and the question this answers is whether the viewer has had a moment
  // of grid since the last one. A ref, because it has to outlive the flash it
  // is measuring without causing a render of its own.
  const clearedAtRef = useRef<number | null>(null)
  const [flash, setFlash] = useState<PickFlashState | null>(null)

  useEffect(() => {
    if (!picks) return

    const made = new Set<number>()
    let newest: PickState | null = null
    const previous = seenRef.current

    for (const pick of picks) {
      if (pick.status !== 'made') continue
      made.add(pick.overall)
      if (previous && !previous.has(pick.overall)) {
        if (!newest || pick.overall > newest.overall) newest = pick
      }
    }

    seenRef.current = made
    if (newest) {
      const landed = newest
      const clearedAt = clearedAtRef.current
      const now = Date.now()
      setFlash((current) => ({
        pick: landed,
        // Two ways the grid can have been uncovered long enough to earn a drum
        // roll, and *both* are required.
        //
        // `current == null` — no flash is on screen right now. Without this the
        // storm case reads backwards: a pick landing mid-flash is compared
        // against a clearedAt from before the flash it is interrupting, so the
        // seconds the board spent covered count as quiet time and the bar plays
        // again. It also covers the storm that starts on the session's first
        // pick, where no flash has ever cleared and clearedAt is still null.
        //
        // Then the clock: no previous flash at all means the board has been
        // quiet since it loaded, which is exactly right for the first pick of
        // the night.
        preroll: current == null && (clearedAt == null || now - clearedAt >= QUIET_MS),
      }))
    }
  }, [picks])

  useEffect(() => {
    if (!flash) return
    const id = setTimeout(
      () => {
        // Stamped here rather than in the cleanup below. The cleanup also runs
        // when a *new* pick replaces this flash, and stamping there would record
        // a clear that never happened — handing the next pick a full sequence in
        // the middle of a storm, which is precisely backwards.
        clearedAtRef.current = Date.now()
        setFlash(null)
      },
      flash.preroll ? FULL_MS : FAST_MS,
    )
    return () => clearTimeout(id)
  }, [flash])

  return flash
}
